/**
 * Item listing — grid or list of products with optional toolbar and row actions
 */

import type React from 'react';
import { Link } from 'react-router';

import type { Product } from '../../types';
import { ProductGrid } from '../ProductGrid/ProductGrid';

import * as styles from './ItemListing.css';

export type ListingViewMode = 'grid' | 'list';

interface ItemListingProps {
  products: Product[];
  viewMode: ListingViewMode;
  onViewModeChange?: (mode: ListingViewMode) => void;
  showDescription?: boolean;
  listActions?: (product: Product) => React.ReactNode;
  showViewToggle?: boolean;
  className?: string;
  meta?: React.ReactNode;
  children?: React.ReactNode;
}

export const ItemListing: React.FC<ItemListingProps> = ({
  products,
  viewMode,
  onViewModeChange,
  showDescription = false,
  listActions,
  showViewToggle = true,
  className,
  meta,
  children,
}) => {
  const showToolbar = Boolean(children) || (showViewToggle && onViewModeChange);

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {showToolbar && (
        <div className={styles.controls}>
          {children}
          {showViewToggle && onViewModeChange && (
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={`${styles.toggleButton} ${
                  viewMode === 'grid' ? styles.toggleButtonActive : ''
                }`}
                onClick={() => onViewModeChange('grid')}
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
              >
                ⊞
              </button>
              <button
                type="button"
                className={`${styles.toggleButton} ${
                  viewMode === 'list' ? styles.toggleButtonActive : ''
                }`}
                onClick={() => onViewModeChange('list')}
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
              >
                ☰
              </button>
            </div>
          )}
        </div>
      )}

      {meta}

      {viewMode === 'list' && products.length > 0 ? (
        <div className={styles.listContainer}>
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className={styles.listItem}
            >
              <img
                src={product.images[0]}
                alt={product.name}
                className={styles.listThumbnail}
              />
              <div className={styles.listDetails}>
                <p className={styles.listCategory}>{product.category}</p>
                <h3 className={styles.listName}>{product.name}</h3>
                {showDescription && (
                  <p className={styles.listDescription}>
                    {product.description}
                  </p>
                )}
              </div>
              {listActions && (
                <div
                  className={styles.listActions}
                  onClick={(event) => event.preventDefault()}
                >
                  {listActions(product)}
                </div>
              )}
            </Link>
          ))}
        </div>
      ) : (
        <ProductGrid products={products} />
      )}
    </div>
  );
};
